import { cn } from '@/lib/utils';
import {
  ButtonHTMLAttributes,
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  ReactElement,
} from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', asChild = false, children, ...props }, ref) => {
    const classes = cn(
      'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      {
        'bg-[#990000] text-white hover:bg-[#800000] focus-visible:ring-[#990000]':
          variant === 'primary',
        'bg-[#e69138] text-white hover:bg-[#d17f2a] focus-visible:ring-[#e69138]':
          variant === 'secondary',
        'border border-[#f1c232] bg-[#f1c232] text-gray-900 hover:bg-[#f1c232]/90 focus-visible:ring-[#f1c232]':
          variant === 'outline',
        'hover:bg-[#f1c232]/10 hover:text-[#f1c232] focus-visible:ring-[#f1c232]':
          variant === 'ghost',
        'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600':
          variant === 'danger',
        'h-8 px-3 text-sm': size === 'sm',
        'h-10 px-4': size === 'md',
        'h-12 px-6 text-lg': size === 'lg',
        'h-10 w-10 p-0': size === 'icon',
      },
      className
    );

    if (asChild && isValidElement(children)) {
      const child = Children.only(children) as ReactElement<{ className?: string }>;

      return cloneElement(child, {
        ...props,
        ref,
        className: cn(classes, child.props.className),
      } as Partial<typeof child.props> & { ref: typeof ref });
    }

    return (
      <button ref={ref} className={classes} {...props}>
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
